<template>
  <div class="teacher-app-page add-student-page" v-if="selectedClassRoom">
    <Toolbar />

    <v-container class="add-student-page__content" fluid>
      <header class="add-student-heading">
        <button type="button" aria-label="Back to home" @click="backToHome"><v-icon>mdi-chevron-left</v-icon></button>
        <div>
          <p class="teacher-page-eyebrow">Classroom</p>
          <h1>Add a student</h1>
          <p>Find a student and add them to {{ selectedClassRoom.ClassRoom.name }}.</p>
        </div>
      </header>

      <section class="add-student-destination">
        <span><v-icon color="#2f7d4a">mdi-google-classroom</v-icon></span>
        <div><small>Adding to</small><strong>{{ selectedClassRoom.ClassRoom.name }}</strong></div>
        <em>{{ StudentsInThisClass.length }} {{ StudentsInThisClass.length === 1 ? "student" : "students" }}</em>
      </section>

      <section class="add-student-picker-card">
        <div class="add-student-picker-card__heading">
          <div><h2>Select a student</h2><p>Assignment status is shown below each name.</p></div>
        </div>

        <label class="add-student-field-label" for="add-student-select">Search students</label>
        <v-autocomplete
          id="add-student-select"
          v-model="selectedStudent"
          :items="AllStudents"
          :loading="loadingStudents"
          :item-text="studentName"
          return-object
          clearable
          outlined
          hide-details
          prepend-inner-icon="mdi-magnify"
          placeholder="Type a student’s name"
          no-data-text="No matching students"
          :menu-props="{ offsetY: true, nudgeBottom: 7, maxHeight: 320 }"
        >
          <template v-slot:item="{ item }">
            <div class="add-student-option">
              <span class="teacher-student-row__avatar">{{ initials(studentName(item)) }}</span>
              <span class="add-student-option__copy">
                <strong>{{ studentName(item) }}</strong>
                <small v-if="currentRoomName(item)">Currently in {{ currentRoomName(item) }}</small>
                <small v-else>Not assigned to a classroom</small>
              </span>
              <span class="add-student-option__status" :class="{ 'add-student-option__status--assigned': currentRoomName(item) }">
                {{ currentRoomName(item) ? "Assigned" : "Available" }}
              </span>
            </div>
          </template>
        </v-autocomplete>

        <div v-if="selectedStudent" class="add-student-selection" :class="{ 'add-student-selection--move': isSelectedAssigned }">
          <div class="add-student-selection__top">
            <span class="teacher-student-row__avatar">{{ initials(studentName(selectedStudent)) }}</span>
            <div><strong>{{ studentName(selectedStudent) }}</strong><small>{{ selectionMessage }}</small></div>
            <v-icon :color="isSelectedAssigned ? '#8a6525' : '#2f7d4a'">{{ isSelectedAssigned ? "mdi-swap-horizontal" : "mdi-check-circle-outline" }}</v-icon>
          </div>
          <div v-if="isSelectedAssigned" class="add-student-move-note">
            <v-icon size="17" color="#8a6525">mdi-information-outline</v-icon>
            <span>You’ll be asked to confirm before moving this student.</span>
          </div>
        </div>

        <v-btn
          class="add-student-submit"
          color="primary"
          x-large
          block
          elevation="0"
          :disabled="!selectedStudent"
          :loading="saving"
          @click="submit"
        >
          <v-icon left size="20">{{ isSelectedAssigned ? "mdi-swap-horizontal" : "mdi-account-plus-outline" }}</v-icon>
          {{ isSelectedAssigned ? "Move to this class" : "Add to class" }}
        </v-btn>
      </section>

      <section class="add-student-current-card">
        <div class="teacher-section-heading">
          <div><h2>Students in this class</h2><p>{{ selectedClassRoom.ClassRoom.name }}</p></div>
          <span class="add-student-current-count">{{ StudentsInThisClass.length }}</span>
        </div>

        <div v-if="StudentsInThisClass.length" class="add-student-current-list">
          <template v-for="row in StudentsInThisClass">
            <div v-if="row.Student" :key="row.id" class="add-student-current-row">
              <span class="teacher-student-row__avatar">{{ initials(row.Student.fullName) }}</span>
              <span><strong>{{ row.Student.fullName }}</strong><small>Currently assigned</small></span>
              <button type="button" :aria-label="`Remove ${row.Student.fullName}`" @click="deleteRow(row)">
                <v-icon size="19" color="#a64a4a">mdi-account-minus-outline</v-icon>
              </button>
            </div>
          </template>
        </div>
        <div v-else class="teacher-empty-state">
          <span><v-icon size="28" color="#2f7d4a">mdi-account-group-outline</v-icon></span>
          <h3>This class is empty</h3>
          <p>Search above to add the first student.</p>
        </div>
      </section>
    </v-container>

    <BottomNavigation />
  </div>
</template>

<script>
import classRoomService from "@/modules/ClassRoom/service.js";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import BottomNavigation from "@/modules/Layout/components/BottomNavigation.vue";

export default {
  name: "add-student-to-class",
  components: { Toolbar, BottomNavigation },
  data() {
    return {
      selectedStudent: null,
      AllStudents: [],
      StudentsInThisClass: [],
      Teacher: null,
      selectedClassRoom: null,
      loadingStudents: true,
      saving: false,
    };
  },
  created() {
    document.title = "taleem - Add Student";
    if (Object.keys(this.$route.params).length === 0) this.$router.push({ name: "home" });
    else {
      this.Teacher = this.$route.params.teacher;
      this.selectedClassRoom = this.$route.params.selectedClassRoom;
      this.StudentsInThisClass = [...(this.$route.params.students || [])];
      this.getAllStudent();
    }
  },
  computed: {
    isSelectedAssigned() {
      return Boolean(this.selectedStudent && this.selectedStudent.ClassRoomStudents && this.selectedStudent.ClassRoomStudents.length);
    },
    selectionMessage() {
      if (!this.selectedStudent) return "";
      if (this.isSelectedAssigned) return `Currently assigned to ${this.currentRoomName(this.selectedStudent)}`;
      return "Available to add to this classroom";
    },
  },
  methods: {
    initials(name) {
      if (!name) return "S";
      return name.split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    },
    studentName(student) {
      if (!student) return "";
      if (student.fullName) return student.fullName;
      return [student.firstName, student.lastName].filter(Boolean).join(" ").trim();
    },
    currentRoomName(student) {
      const assignment = student && student.ClassRoomStudents && student.ClassRoomStudents[0];
      return assignment && assignment.ClassRoom ? assignment.ClassRoom.name : "";
    },
    async deleteRow(row) {
      const result = await this.$swal({
        title: "Remove from class?",
        text: `${row.Student.fullName} will be removed from ${this.selectedClassRoom.ClassRoom.name}. Their student record will remain.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#a64a4a",
        cancelButtonColor: "#2f7d4a",
        cancelButtonText: "Keep student",
        confirmButtonText: "Remove from class",
      });
      if (result.value) {
        await classRoomService.removeStudent(row.id);
        this.StudentsInThisClass = this.StudentsInThisClass.filter(student => student.id !== row.id);
        await this.getAllStudent();
        this.showToast("success", "Student removed from class");
      }
    },
    async submit() {
      if (!this.selectedStudent || this.saving) return;
      if (this.isSelectedAssigned) {
        const currentRoom = this.currentRoomName(this.selectedStudent);
        const result = await this.$swal({
          title: "Move student?",
          text: `${this.studentName(this.selectedStudent)} is currently in ${currentRoom}. Move them to ${this.selectedClassRoom.ClassRoom.name}?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#8a6525",
          cancelButtonColor: "#2f7d4a",
          cancelButtonText: "Keep current class",
          confirmButtonText: "Move student",
        });
        if (!result.value) return;
      }
      await this.assignSelectedStudent();
    },
    async assignSelectedStudent() {
      this.saving = true;
      const selected = this.selectedStudent;
      const wasAssigned = this.isSelectedAssigned;
      try {
        let response;
        response = await classRoomService.transferStudent({
          ClassRoomId: this.selectedClassRoom.ClassRoomId,
          StudentId: selected.id,
        });

        if (response && response.data) response = response.data;
        if (response && response.Student) this.StudentsInThisClass.push(response);
        else {
          this.StudentsInThisClass.push({
            id: response && response.id ? response.id : selected.ClassRoomStudents[0] && selected.ClassRoomStudents[0].id,
            StudentId: selected.id,
            Student: selected,
          });
        }
        this.AllStudents = this.AllStudents.filter(student => student.id !== selected.id);
        this.selectedStudent = null;
        this.showToast("success", wasAssigned ? "Student moved to class" : "Student added to class");
      } catch (error) {
        this.showToast("error", error && error.data ? error.data.message : "Student could not be added");
      } finally {
        this.saving = false;
      }
    },
    showToast(icon, title) {
      this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 }).fire({ icon, title });
    },
    backToHome() {
      this.$router.push({ name: "home" });
    },
    getAllStudent() {
      this.loadingStudents = true;
      return classRoomService.getTransferCandidates({ ClassRoomId: this.selectedClassRoom.ClassRoomId }).then(result => {
        const currentIds = new Set(this.StudentsInThisClass.map(row => row.StudentId));
        this.AllStudents = result.data.filter(student => !currentIds.has(student.id));
        return result;
      }).finally(() => {
        this.loadingStudents = false;
      });
    },
  },
};
</script>
